import axios from "axios";

// For local testing: http://localhost:8080/api/v1
// For production: https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Get authentication token from localStorage
 */
function getToken() {
  return localStorage.getItem("token");
}

/**
 * Get all games for admin to choose from
 */
export async function getAllGames(params = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/games`,
      {
        params,
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching games:", error);
    throw error;
  }
}

export async function getPendingAssignmentQueue() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/games/pending-assignments`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      const fallback = await getAllGames({ status: "pending", limit: 50 });
      const games = (fallback.games || [])
        .filter((game) => game.isPublicPlaytest !== true && game.playtestType !== "free_public")
        .map((game) => ({
          gameId: game._id || game.gameId,
          name: game.name,
          playtestType: game.playtestType || null,
          numPlaytesters: Number(game.numPlaytesters || 0),
          assignedCount: game.stats?.totalAssignments || 0,
          needsCount: Math.max(
            0,
            Number(game.numPlaytesters || 0) - (game.stats?.totalAssignments || 0)
          ),
          developer: game.developer || {},
          createdAt: game.createdAt,
        }));
      return { total: games.length, games };
    }
    console.error("Error fetching pending assignment queue:", error);
    throw error;
  }
}

/**
 * Enable or disable a game on the Public Playtest tab
 * @param {number|string} gameId
 * @param {boolean} enabled
 */
export async function setGamePublicPlaytest(gameId, enabled) {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/games/${gameId}/public-playtest`,
      { enabled: !!enabled },
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating public playtest flag:", error);
    throw error;
  }
}

/**
 * Get recommended playtesters for a specific game (Smart Match)
 */
export async function getRecommendedPlaytesters(gameId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/playtesters/available-for-game/${gameId}`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching recommended playtesters:", error);
    throw error;
  }
}

/**
 * Search playtesters by query
 */
export async function searchPlaytesters(query) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/playtesters/search`,
      {
        params: { q: query },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error searching playtesters:", error);
    throw error;
  }
}

/**
 * Get all playtesters
 */
export async function getAllPlaytesters(params = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/playtesters`,
      {
        params,
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching all playtesters:", error);
    throw error;
  }
}

/**
 * Get playtester details by ID
 */
export async function getPlaytesterDetails(playtesterId) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/playtesters/${playtesterId}`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching playtester details:", error);
    throw error;
  }
}

/**
 * Manually assign playtesters to a game
 */
export async function manualAssignPlaytesters(assignmentData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/assignments/manual`,
      assignmentData,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error manually assigning playtesters:", error);
    throw error;
  }
}

/**
 * Get all assignments (for monitoring)
 */
export async function getAllAssignments(filters = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/assignments`,
      {
        params: filters,
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching assignments:", error);
    throw error;
  }
}

/**
 * Assignment metrics including play progress counts
 */
export async function getAssignmentMetrics() {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/assignments/metrics`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching assignment metrics:", error);
    throw error;
  }
}

/**
 * Update assignment status
 */
export async function updateAssignmentStatus(assignmentId, status, reason) {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/assignments/${assignmentId}/status`,
      { status, reason },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating assignment status:", error);
    throw error;
  }
}

/**
 * Remove / cancel an assignment
 */
export async function removeAssignment(assignmentId, reason = "Removed by admin") {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/assignments/${assignmentId}`,
      {
        data: { reason },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error removing assignment:", error);
    throw error;
  }
}

/**
 * Send play-reminder emails to not-started assignees
 */
export async function remindAssignments({ assignmentIds, gameId, customMessage, force } = {}) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/assignments/remind`,
      { assignmentIds, gameId, customMessage, force },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error sending play reminders:", error);
    throw error;
  }
}

/**
 * Reassign a playtester
 */
export async function reassignPlaytester(assignmentId, reassignmentData) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/assignments/${assignmentId}/reassign`,
      reassignmentData,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error reassigning playtester:", error);
    throw error;
  }
}

